/**
 * taskShared.js
 *
 * Constants, formatters and the small presentational pieces used by both the
 * board (Dashboard.js) and the single-task page (TaskDetail.js).
 *
 * The presentational components live at module scope on purpose. A component
 * declared inside another component is a new type on every render, which
 * unmounts and remounts its subtree - and a focused input inside it loses
 * focus after a single keystroke.
 */

import { Box, Typography, alpha } from "@mui/material";

export const STAFF_IMAGE_BASE =
  "https://mindsinaction.com.na/assets/media/pictures/";

export const PRIORITIES = [
  { value: "low", label: "Low" },
  { value: "normal", label: "Normal" },
  { value: "high", label: "High" },
  { value: "urgent", label: "Urgent" },
];

// Folders are where a task is FILED. status is its lifecycle, driven by the
// rollup cron from subtasks. They are deliberately independent: a task can
// sit in "Design review" and be inprogress. Both are true.
export const STAGES = [
  { value: "active", label: "To do" },
  { value: "inprogress", label: "In progress" },
  { value: "overdue", label: "Overdue" },
  { value: "onhold", label: "On hold" },
  { value: "completed", label: "Completed" },
];

/**
 * Stages the system sets itself, from the dates. Everything else is a
 * decision somebody made, and automation must not overrule it:
 *
 *   onhold     - a deliberate pause. Only a person moves it in or out.
 *   completed  - finished. Only a person, or the subtask rollup, sets it.
 */
export const AUTO_STAGES = ["active", "inprogress", "overdue"];

export const isAutoStage = (status) => AUTO_STAGES.includes(status);

/**
 * Folders and status are two views of the same thing: the folder a task sits
 * in IS its stage. Matching is by folder NAME, because folder ids are
 * per-account while the seeded names are shared.
 *
 * A folder with no entry here (someone's "Design review") carries no stage
 * meaning, and filing a task there leaves its status alone.
 */
export const FOLDER_KIND = {
  "to do": "todo",
  todo: "todo",
  "in progress": "inprogress",
  inprogress: "inprogress",
  completed: "completed",
  complete: "completed",
  done: "completed",
  overdue: "overdue",
  "over due": "overdue",
  "on hold": "onhold",
  onhold: "onhold",
  held: "onhold",
  archive: "archive",
  archived: "archive",
};

// The five the board sets up for a new account, in column order.
export const DEFAULT_FOLDERS = [
  "To do",
  "In progress",
  "Overdue",
  "On hold",
  "Completed",
  "Archive",
];

export const normaliseName = (name) =>
  String(name || "").trim().toLowerCase().replace(/\s+/g, " ");

export const folderKind = (folder) =>
  FOLDER_KIND[normaliseName(folder?.name)] || null;

/**
 * The stage a folder represents, or null if it carries none.
 *
 * Overdue holds work that is still in progress - overdue is derived from the
 * deadline, never stored, so it cannot go stale. Archive is a container, not
 * a stage: what is in it keeps whatever status it finished with.
 */
export const statusForFolder = (folder) => {
  switch (folderKind(folder)) {
    case "todo":
      return "active";
    case "inprogress":
      return "inprogress";
    case "overdue":
      return "overdue";
    case "onhold":
      return "onhold";
    case "completed":
      return "completed";
    // Archive is a container, not a stage: what is in it keeps the status it
    // finished with.
    default:
      return null;
  }
};

/**
 * Only root folders count as stages - a subfolder someone happens to call
 * "To do" inside "Q4 work" is their own filing, not a board column.
 */
export const folderOfKind = (folders, kind) =>
  (folders || []).find(
    (f) => Number(f.parent_id ?? 0) === 0 && folderKind(f) === kind
  ) || null;

/**
 * The folder a given stage belongs in. inprogress resolves to "In progress",
 * never to Overdue, even though both carry that status - Overdue is reached
 * by being late, not by being in progress.
 */
const KIND_FOR_STATUS = {
  active: "todo",
  inprogress: "inprogress",
  overdue: "overdue",
  onhold: "onhold",
  completed: "completed",
};

export const folderForStatus = (folders, status) =>
  folderOfKind(folders, KIND_FOR_STATUS[status] || "inprogress");

/**
 * The stage a task SHOULD be at, given the date. The single rule the board,
 * the API and the cron all follow, so they cannot drift apart.
 *
 *   past its deadline          -> overdue
 *   started, not yet due       -> inprogress
 *   starts later               -> active  (To do)
 *
 * Completed and on-hold tasks are returned untouched: one is finished, the
 * other is a deliberate pause, and neither is the clock's business. That is
 * what makes "start tomorrow, find it In progress tomorrow" safe to run on
 * every load - it can only ever move work between the three auto stages.
 */
export const derivedStatus = (task, todayStr) => {
  const current = task?.status || "active";
  if (!isAutoStage(current)) return current;

  const start = String(task?.start_date || "").slice(0, 10);
  const deadline = String(task?.deadline || "").slice(0, 10);

  if (deadline && deadline < todayStr) return "overdue";
  if (!start || start <= todayStr) return "inprogress";
  return "active";
};

/** Where a brand new task begins, from its dates. */
export const statusFromDates = (startDate, todayStr, deadline) =>
  derivedStatus({ status: "active", start_date: startDate, deadline }, todayStr);

/** Is this task filed inside Archive, at any depth? */
export const isArchived = (task, folders) => {
  const archive = folderOfKind(folders, "archive");
  if (!archive) return false;

  let cursor = String(task?.folder_id ?? "0");
  const seen = new Set();
  while (cursor && cursor !== "0" && !seen.has(cursor)) {
    if (String(cursor) === String(archive.id)) return true;
    seen.add(cursor);
    const parent = (folders || []).find((f) => String(f.id) === cursor);
    cursor = parent ? String(parent.parent_id ?? "0") : "0";
  }
  return false;
};

/**
 * Which column a task belongs in, all things considered.
 *
 * Archived work is left exactly where it was put - the year folders are the
 * point of archiving, so re-filing them would undo it.
 *
 * Otherwise: late and unfinished outranks everything, because that is the
 * thing somebody needs to see.
 */
export const homeFolderForTask = (task, folders, todayStr) => {
  if (!task) return null;
  // Archived work is left exactly where it was put - the year folders are the
  // whole point, so re-filing them would undo the archive.
  if (isArchived(task, folders)) return null;

  return folderForStatus(folders, derivedStatus(task, todayStr));
};

export const PERMISSION_LABEL = {
  view: "View only",
  edit: "View & edit",
  full: "Full rights",
};

export const EMPTY_PROGRESS = {
  subtask_id: "0",
  log_date: "",
  hours_spent: "1",
  progress_percent: "",
  work_done: "",
  blockers: "",
};

/* ─── Helpers ──────────────────────────────────────────────────────────── */

export const today = () => new Date().toISOString().split("T")[0];

export const num = (v) => parseFloat(v) || 0;

export const fmtDate = (v) => {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

export const daysBetween = (from, to) => {
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  const am = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const bm = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((bm - am) / 86400000);
};

export const isOverdue = (t) =>
  t?.status !== "completed" && t?.deadline && daysBetween(today(), t.deadline) < 0;

export const initials = (first, last) =>
  `${first?.[0] || ""}${last?.[0] || ""}`.toUpperCase() || "?";

export const atLeast = (have, need) => {
  const rank = { view: 1, edit: 2, full: 3 };
  return (rank[have] || 0) >= (rank[need] || 0);
};

export const stageLabel = (value) =>
  STAGES.find((s) => s.value === value)?.label || value || "—";

/* ─── Theme tokens ─────────────────────────────────────────────────────── */

/**
 * The one place the page palette is decided, so the board and the detail
 * page cannot drift apart. `colors` is the output of tokens(mode).
 */
export const buildUi = (colors, isDark) => {
  const accent = colors.teal[500];
  const accent2 = colors.newTeal[500];
  return {
    accent,
    accent2,
    pageBg: isDark ? colors.dark[800] : "#e9ebef",
    surface: isDark ? colors.dark[700] : colors.white[900],
    surfaceAlt: isDark ? colors.dark[600] : colors.white[600],
    border: isDark
      ? alpha(colors.white[500], 0.09)
      : alpha(colors.dark[500], 0.1),
    textPrimary: colors.primary[100],
    textSecondary: isDark ? colors.gray[400] : colors.gray[300],
    gradient: `linear-gradient(135deg, ${accent} 0%, ${accent2} 100%)`,
    shadow: isDark
      ? "0 14px 34px -22px rgba(0,0,0,0.8)"
      : "0 14px 34px -24px rgba(16,24,74,0.35)",
  };
};

export const priorityColor = (colors, p) =>
  ({
    low: colors.gray[500],
    normal: colors.newTeal[500],
    high: colors.orange[500],
    urgent: colors.red[500],
  }[p] || colors.gray[500]);

/* ─── Presentational components ────────────────────────────────────────── */

export const SectionCard = ({ ui, title, hint, action, children }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      boxShadow: ui.shadow,
      p: { xs: 2, md: 3 },
      mb: 3,
    }}
  >
    <Box
      display="flex"
      alignItems="flex-start"
      justifyContent="space-between"
      gap={2}
      mb={hint ? 0.5 : 2}
    >
      <Typography variant="h6" fontWeight={700} color={ui.textPrimary}>
        {title}
      </Typography>
      {action}
    </Box>
    {hint && (
      <Typography variant="body2" color={ui.textSecondary} mb={2}>
        {hint}
      </Typography>
    )}
    {children}
  </Box>
);

export const DetailRow = ({ ui, label, value, color }) => (
  <Box
    display="flex"
    flexWrap="wrap"
    gap={1}
    py={1.2}
    borderBottom={`1px solid ${alpha(ui.border, 0.6)}`}
  >
    <Typography
      variant="body2"
      color={ui.textSecondary}
      sx={{ minWidth: 150, fontWeight: 600 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{ color: color || ui.textPrimary, flex: 1, whiteSpace: "pre-wrap" }}
    >
      {value || "—"}
    </Typography>
  </Box>
);

export const StatCard = ({ ui, icon, label, value, sub, tone }) => (
  <Box
    sx={{
      bgcolor: ui.surface,
      border: `1px solid ${ui.border}`,
      borderRadius: "14px",
      p: 2,
      height: "100%",
      boxShadow: ui.shadow,
    }}
  >
    <Box display="flex" alignItems="center" gap={1.2} mb={1}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          display: "grid",
          placeItems: "center",
          color: tone || ui.accent,
          bgcolor: alpha(tone || ui.accent, 0.14),
        }}
      >
        {icon}
      </Box>
      <Typography variant="body2" color={ui.textSecondary} fontWeight={600}>
        {label}
      </Typography>
    </Box>
    <Typography variant="h4" fontWeight={700} color={ui.textPrimary}>
      {value}
    </Typography>
    {sub && (
      <Typography variant="caption" color={ui.textSecondary}>
        {sub}
      </Typography>
    )}
  </Box>
);
